import { WordMatItem } from '@/common/types/wordMat';
import { UniqueIdentifier, useDraggable } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { XIcon } from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shadcn/components/dropdown-menu';
import { CSSProperties, useState } from 'react';

export type { WordMatItem };

export function PaletteItem({
  item,
  children,
}: {
  item: WordMatItem;
  children: React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: item.id,
    data: {
      source: 'palette',
      item,
    },
  });

  let opacity = 1;
  if (isDragging) opacity = 0.5;
  if (String(item.id).startsWith('preview')) opacity = 0.5;

  const style: CSSProperties = {
    cursor: isDragging ? 'grabbing' : 'grab',
    opacity,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
}

export function CustomPaletteItem({
  item,
  children,
  onRemove,
}: {
  item: WordMatItem;
  children: React.ReactNode;
  onRemove: (itemId: UniqueIdentifier) => void;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: item.id,
    data: {
      source: 'palette',
      item,
    },
  });

  let opacity = 1;
  if (isDragging) opacity = 0.5;
  if (String(item.id).startsWith('preview')) opacity = 0.5;

  const style: CSSProperties = {
    cursor: isDragging ? 'grabbing' : 'grab',
    opacity,
  };

  return (
    <div className="relative group">
      <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
        {children}
      </div>
      <button
        className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 hover:bg-red-600 rounded-full opacity-0 group-hover:opacity-80 hover:opacity-100 transition-all duration-200 hover:scale-110 border border-white shadow-sm flex items-center justify-center"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onRemove(item.id);
        }}
      >
        <XIcon size={12} weight="bold" className="text-white" />
      </button>
    </div>
  );
}

export function MatItem({
  item,
  children,
  onDiacriticChange,
  language,
}: {
  item: WordMatItem;
  children: React.ReactNode;
  onDiacriticChange?: (itemId: UniqueIdentifier, newText: string) => void;
  language?: 'en' | 'es' | 'fr';
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, data: { source: 'mat', item } });

  const [showDiacritics, setShowDiacritics] = useState(false);

  let opacity = 1;
  if (isDragging) opacity = 0.5;
  if (String(item.id).startsWith('preview')) opacity = 0.5;

  const style: CSSProperties = {
    transform: CSS.Translate.toString(transform),
    cursor: isDragging ? 'grabbing' : 'grab',
    opacity,
    transition,
  };

  const baseLetter = item.text.toLowerCase().normalize('NFD')[0]; // Get base character without diacritics
  const isSpanishVowel = ['a', 'e', 'i', 'o', 'u'].includes(baseLetter);
  const isFrenchVowel = ['a', 'e', 'i', 'o', 'u', 'y'].includes(baseLetter);
  const isSingleLetter = item.text.length === 1;
  const canHaveDiacritics =
    ((language === 'es' && isSpanishVowel) ||
      (language === 'fr' && isFrenchVowel)) &&
    isSingleLetter &&
    onDiacriticChange;

  const spanishDiacriticOptions: Record<string, string[]> = {
    a: ['á'],
    e: ['é'],
    i: ['í'],
    o: ['ó'],
    u: ['ú', 'ü'],
  };

  const frenchDiacriticOptions: Record<string, string[]> = {
    a: ['à', 'â'],
    e: ['é', 'è', 'ê', 'ë'],
    i: ['î', 'ï'],
    o: ['ô'],
    u: ['ù', 'û', 'ü'],
    y: ['ÿ'],
  };

  const diacriticOptions =
    language === 'fr' ? frenchDiacriticOptions : spanishDiacriticOptions;

  const handleDiacriticSelect = (newText: string) => {
    onDiacriticChange?.(item.id, newText);
    setShowDiacritics(false);
  };

  if (canHaveDiacritics) {
    return (
      <div className="relative group">
        <div
          ref={setNodeRef}
          style={style}
          {...attributes}
          {...listeners}
          className="relative"
        >
          {children}
        </div>
        <DropdownMenu open={showDiacritics} onOpenChange={setShowDiacritics}>
          <DropdownMenuTrigger asChild>
            <button
              className="absolute -top-3 -right-3 w-6 h-6 bg-blue-500 hover:bg-blue-600 rounded-full opacity-0 group-hover:opacity-80 hover:opacity-100 transition-all duration-200 hover:scale-110 border border-white shadow-sm"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowDiacritics(true);
              }}
            />
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            {diacriticOptions[baseLetter as keyof typeof diacriticOptions]?.map(
              (option) => {
                const finalOption =
                  item.text === item.text.toUpperCase()
                    ? option.toUpperCase()
                    : option;
                return (
                  <DropdownMenuItem
                    key={option}
                    onClick={() => handleDiacriticSelect(finalOption)}
                    className="text-center font-medium font-['Aronui']"
                    style={{ fontFamily: 'Aronui' }}
                  >
                    {finalOption}
                  </DropdownMenuItem>
                );
              },
            )}
            <DropdownMenuItem
              onClick={() => {
                const isUpperCase = item.text === item.text.toUpperCase();
                const cleanVowel = isUpperCase
                  ? baseLetter.toUpperCase()
                  : baseLetter;
                handleDiacriticSelect(cleanVowel);
              }}
              className="text-center font-medium font-['Aronui']"
              style={{ fontFamily: 'Aronui' }}
            >
              {item.text === item.text.toUpperCase()
                ? baseLetter.toUpperCase()
                : baseLetter}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
}
